import {
  affiliateUrl as buildAffiliateUrl,
  ga4Param,
  isAffiliateUrl,
  merchantHostOf,
} from '@cch/shared/affiliate';
import { buildXcust } from '@cch/shared/xcust';
import * as Sentry from '@sentry/react';
import { trackEvent } from './analytics';

const SKIMLINKS_SITE_ID = import.meta.env.VITE_SKIMLINKS_SITE_ID || '';

// Module-level state set by AuthContext and the UTM bootstrap in main.tsx.
// Read at click time so every outbound affiliate URL gets the current
// customer + cohort, without each call site having to thread them through.
let currentCustomerId: string | null = null;
let currentCohort: string | null = null;

const ANON_ID_STORAGE_KEY = 'cch.anon_id';
const COHORT_STORAGE_KEY = 'cch.utm_cohort';

export function setAffiliateCustomerId(id: string | null): void {
  currentCustomerId = id;
}

export function setAffiliateCohort(cohort: string | null): void {
  currentCohort = cohort;
}

function readAnonId(): string {
  try {
    const existing = window.localStorage.getItem(ANON_ID_STORAGE_KEY);
    if (existing) return existing;
    const crypto = window.crypto;
    const uuid = typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    const next = `anon_${uuid}`;
    window.localStorage.setItem(ANON_ID_STORAGE_KEY, next);
    return next;
  } catch {
    // Private mode / disabled storage — fall back to an ephemeral ID. Loses
    // cross-session continuity but keeps the cohort attribution working.
    return `anon_${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Reads UTM params from the current location (or sessionStorage if a cohort
 * was already captured this session) and stores the resulting cohort label.
 * Call once at app boot, before the first affiliate click.
 *
 * Cohort format: `<utm_campaign>-<utm_source>` (either side may be missing).
 */
export function initAffiliateCohort(): void {
  try {
    const cached = window.sessionStorage.getItem(COHORT_STORAGE_KEY);
    if (cached) {
      currentCohort = cached;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source') ?? '';
    const campaign = params.get('utm_campaign') ?? '';
    if (!source && !campaign) return;
    const cohort = [campaign, source].filter(Boolean).join('-').toLowerCase();
    if (!cohort) return;
    window.sessionStorage.setItem(COHORT_STORAGE_KEY, cohort);
    currentCohort = cohort;
  } catch {
    // Ignore — storage / location access is best-effort.
  }
}

function xcustFor(productCustomId: string | undefined): string {
  // Resolve the customer field: real customer ID > anon ID (only when a UTM
  // cohort exists, so we don't tag every random visitor) > empty.
  let customerId: string | null = null;
  if (currentCustomerId) {
    customerId = currentCustomerId;
  } else if (currentCohort) {
    customerId = readAnonId();
  }

  // Always emitted, even with nothing else to say: the `web` surface marker is
  // what splits a web sale from an app sale in the commission report.
  return buildXcust({
    surface: 'web',
    productId: productCustomId,
    customerId,
    cohort: currentCohort,
  });
}

/**
 * Wraps a merchant URL with Skimlinks Link Wrapper redirect for affiliate tracking.
 * Returns the original URL unchanged if the Site ID is not configured or the URL is empty.
 *
 * `xcust` is `web|<product>|<customer-or-anon>[|<utm-cohort>]`, built by
 * `buildXcust` in `@cch/shared/xcust` within Skimlinks' 50-character limit.
 * Customer ID comes from {@link setAffiliateCustomerId} (called by AuthContext).
 * Cohort comes from {@link initAffiliateCohort} (called once at app boot).
 *
 * @param url - The raw merchant/retailer URL
 * @param productCustomId - The product id; its first 12 letters and digits
 *                          become the product field of `xcust`.
 */
export function affiliateUrl(url: string, productCustomId?: string): string {
  const wrapped = buildAffiliateUrl(url, {
    siteId: SKIMLINKS_SITE_ID,
    customId: xcustFor(productCustomId),
  });

  // MAR-126: alarm on a link that will open unwrapped. See
  // `reportUnwrappedLink` — placeholders and empty URLs aren't outbound links.
  if (url && url !== '#' && !isAffiliateUrl(wrapped)) {
    try {
      reportUnwrappedLink(url);
    } catch {
      // Alerting must never break link rendering.
    }
  }

  return wrapped;
}

/**
 * MAR-126 — unwrapped-link alarm.
 *
 * `VITE_SKIMLINKS_SITE_ID` is baked in at build time, so a Cloudflare Pages env
 * change plus a redeploy can silently strip affiliate attribution site-wide.
 * There is no symptom: links still work, customers still shop, we just earn
 * nothing. This is the only signal.
 *
 * It checks the OUTCOME (`isAffiliateUrl` on the URL we're about to put in the
 * href), not the config — a missing Site ID is only one way to end up
 * unwrapped, and the outcome check catches all of them.
 *
 * Web fires at link-BUILD time rather than click time, which is where the two
 * surfaces differ: an unwrapped href is a loss the moment it renders, and this
 * way we learn about it from the first Shop page view instead of waiting for
 * somebody to click. Mobile has no equivalent moment (the URL is built inside
 * the tap handler), so it reports there.
 *
 * Volume control: one Sentry event per browser session, plus an explicit
 * fingerprint so anything slipping past the gate still groups into one issue.
 * A product grid builds dozens of hrefs per render; without the gate a broken
 * deploy would burn the quota and hide the real errors — worse than no alert.
 * The count keeps rising and rides along as Sentry context, so later events
 * from the same session carry the true tally.
 *
 * The gate is held in `sessionStorage`, NOT in a module-level flag. A module
 * flag resets on every hard page load — it survives SPA navigation and
 * remounts but not a reload — so a sitewide unwrap would file one event per
 * page load per visitor, which on a 5k-events/month tier burns the quota in
 * days and suppresses the real errors this alert must not hide. Same storage
 * and the same consent posture as the UTM cohort above. Scope is the tab: a
 * new tab is a new session and gets one more event, which is the correct
 * reading of "session" and matches what Sentry itself counts.
 */
const UNWRAPPED_REPORTED_KEY = 'cch.affiliate_unwrapped_reported';
const UNWRAPPED_COUNT_KEY = 'cch.affiliate_unwrapped_count';

/**
 * In-memory mirrors. They are the fallback when storage is unavailable
 * (private mode, disabled storage): the gate then degrades to once per page
 * load rather than failing open and reporting on every link.
 */
let unwrappedLinkCount = 0;
let unwrappedReported = false;

function bumpUnwrappedCount(): number {
  unwrappedLinkCount += 1;
  try {
    const stored = Number(window.sessionStorage.getItem(UNWRAPPED_COUNT_KEY));
    const next = (Number.isFinite(stored) ? stored : 0) + 1;
    window.sessionStorage.setItem(UNWRAPPED_COUNT_KEY, String(next));
    return next;
  } catch {
    return unwrappedLinkCount;
  }
}

function hasReportedThisSession(): boolean {
  if (unwrappedReported) return true;
  try {
    return window.sessionStorage.getItem(UNWRAPPED_REPORTED_KEY) === '1';
  } catch {
    return false;
  }
}

function markReportedThisSession(): void {
  unwrappedReported = true;
  try {
    window.sessionStorage.setItem(UNWRAPPED_REPORTED_KEY, '1');
  } catch {
    // Storage unavailable — the module flag still caps this page load.
  }
}

/** Host only — never the full URL (see the xcust note in `reportUnwrappedLink`). */
function merchantHost(url: string): string {
  try {
    return new URL(url, window.location.origin).host || 'unknown';
  } catch {
    return 'unknown';
  }
}

function reportUnwrappedLink(rawUrl: string): void {
  const count = bumpUnwrappedCount();
  // A grid builds one href per card, so warn on the first only — dev output
  // that repeats 24 times per render is output nobody reads.
  if (unwrappedLinkCount === 1) warnUnwrappedInDev(rawUrl);

  const payload = {
    // NEVER the full URL and NEVER the customer id: `xcust` carries a
    // customer id prefix (see `@cch/shared/xcust`), so the wrapped URL — and
    // anything derived from it — would carry the customer id into Sentry. The
    // host tells "one bad merchant URL" from "every link is unwrapped", which
    // is all triage needs.
    merchant_host: merchantHost(rawUrl),
    platform: 'web',
    app_version: import.meta.env.VITE_APP_VERSION ?? 'unknown',
    // Separates "no Site ID in this build" (whole site earns nothing) from
    // "Site ID present but this URL still came back unwrapped".
    site_id_present: SKIMLINKS_SITE_ID.length > 0,
    unwrapped_links_this_session: count,
  };

  // `setContext` writes to the GLOBAL scope, so this block rides along on every
  // Sentry event for the rest of the page life. Named `affiliate_unwrapped`
  // rather than `affiliate` so someone triaging an unrelated crash reads it as
  // "this session also had unwrapped links", not as part of that crash.
  Sentry.setContext('affiliate_unwrapped', payload);

  if (hasReportedThisSession()) return;

  // Sentry is initialised late (ConsentProvider, after the consent check), and
  // captureMessage before init is dropped on the floor. Staying "unreported"
  // until a client exists means the first link rendered pre-consent doesn't
  // burn the one report we get for the session.
  if (!Sentry.getClient()) return;
  markReportedThisSession();

  Sentry.captureMessage(
    'REVENUE LOSS: affiliate link rendered UNWRAPPED, no Skimlinks attribution, so these clicks earn nothing',
    {
      // `warning`, not `error`: nothing is broken for the customer and nothing
      // crashed. The message has to make it read as money walking out of the
      // door to whoever triages it at a glance.
      level: 'warning',
      fingerprint: ['affiliate-link-unwrapped'],
      tags: {
        source: 'affiliate',
        platform: payload.platform,
        site_id_present: String(payload.site_id_present),
        merchant_host: payload.merchant_host,
      },
      contexts: { affiliate_unwrapped: payload },
    },
  );
}

/**
 * Deliberately NOT suppressed in dev.
 *
 * A local `.env` without `VITE_SKIMLINKS_SITE_ID` renders unwrapped links, and
 * that's a real gap someone should be able to trip over rather than one we
 * hide. It costs nothing: Sentry only initialises when `VITE_SENTRY_DSN` is
 * set, which dev doesn't set, so the SDK is inert. The console warning is what
 * a developer actually sees.
 */
function warnUnwrappedInDev(rawUrl: string): void {
  if (!import.meta.env.DEV) return;
  console.warn(
    `[affiliate] ${merchantHost(rawUrl)} link is UNWRAPPED, no Skimlinks attribution. ` +
      (SKIMLINKS_SITE_ID
        ? 'A Site ID is configured, so something else broke the wrap.'
        : 'Set VITE_SKIMLINKS_SITE_ID in .env to wrap links locally.'),
  );
}

export { isAffiliateUrl };

/** Where on the site an outbound click came from, for the `affiliate_click` event. */
export type AffiliateClickSurface = 'card' | 'saved' | 'mum_hub';

/**
 * Fires a GA4 event for an outbound affiliate click.
 * Fails silently if GA4 is not loaded (e.g., ad blocker active).
 *
 * `affiliated` is read off the href the anchor actually navigates to, so it
 * reports the outcome rather than the intent. Every string is held to GA4's
 * 100-character parameter limit, past which the value is lost.
 */
export function trackOutboundClick(product: {
  name: string;
  brand: string;
  slug: string;
  url?: string;
  /** The href the anchor navigates to: wrapped, or the raw merchant URL. */
  href: string;
  surface: AffiliateClickSurface;
}) {
  trackEvent('affiliate_click', {
    event_category: 'outbound',
    event_label: ga4Param(product.name),
    product_brand: ga4Param(product.brand),
    product_slug: ga4Param(product.slug),
    destination_url: ga4Param(product.url ?? ''),
    merchant_host: merchantHostOf(product.url),
    affiliated: isAffiliateUrl(product.href),
    surface: product.surface,
  });
}
